import { Controller } from "@hotwired/stimulus";
import debounce from "debounce";
import asyncFormSubmit from "./helpers/form_submit_helper";

export default class extends Controller {
  static targets = ["fuzzySearchSubmit", "fuzzySearchFilter", "fuzzySearchResultContainer", "group", "searchMode", "modeButton", "actionButton", "actionButtonLabel", "actionButtonIcon", "searchContainer", "componentWrapper"];
  static values = { baseUrl: String };

  private parentGrid: HTMLElement | null = null;

  connect() {
    this.element.addEventListener('keydown', this.handleKeyDown.bind(this), true);
    this.element.addEventListener('submit', this.handleFormSubmit.bind(this), true);
    this.element.addEventListener('turbo:submit-start', this.handleTurboSubmit.bind(this), true);
    this.submit = debounce(this.submit.bind(this), 500);
    this.hideFuzzySearchResultContainerOnBlur = this.hideFuzzySearchResultContainerOnBlur.bind(this);
    this.showFuzzySearchResultContainerOnFocus = this.showFuzzySearchResultContainerOnFocus.bind(this);
    this.addEventListeners();
    this.updateModeButtons();
    this.updatePlaceholder();
    this.updateActionButton();
  }

  disconnect() {
    this.removeEventListeners();
  }

  addEventListeners() {
    document.addEventListener("click", this.hideFuzzySearchResultContainerOnBlur);
    if (this.hasFuzzySearchSubmitTarget) {
      this.fuzzySearchSubmitTarget.addEventListener("input", this.submit);
      this.fuzzySearchSubmitTarget.addEventListener("focusin", this.showFuzzySearchResultContainerOnFocus);
    }
  }

  removeEventListeners() {
    document.removeEventListener("click", this.hideFuzzySearchResultContainerOnBlur);
    if (this.hasFuzzySearchSubmitTarget) {
      this.fuzzySearchSubmitTarget.removeEventListener("input", this.submit);
      this.fuzzySearchSubmitTarget.removeEventListener("focusin", this.showFuzzySearchResultContainerOnFocus);
    }
  }

  hideFuzzySearchResultContainerOnBlur(event) {
    if (this.hasFuzzySearchResultContainerTarget && !this.element.contains(event.target)) {
      this.fuzzySearchResultContainerTarget.style.display = "none";
    }
  }

  handleKeyDown(event) {
    if (event.key === 'Enter' && event.target.tagName !== 'TEXTAREA') {
      const mode = this.hasSearchModeTarget ? this.searchModeTarget.value : "normal";

      if (mode === "ai") {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return false;
      } else {
        event.preventDefault();
      }
    }
  }

  handleFormSubmit(event) {
    const mode = this.hasSearchModeTarget ? this.searchModeTarget.value : "normal";

    if (mode === "ai") {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }
  }

  handleTurboSubmit(event) {
    const mode = this.hasSearchModeTarget ? this.searchModeTarget.value : "normal";

    if (mode === "ai") {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }
  }

  showFuzzySearchResultContainerOnFocus() {
    const mode = this.hasSearchModeTarget ? this.searchModeTarget.value : "normal";
    const hasQuery = this.hasFuzzySearchSubmitTarget && (this.fuzzySearchSubmitTarget as HTMLInputElement).value.trim().length > 0;

    if (mode === "normal" && this.hasFuzzySearchResultContainerTarget && hasQuery) {
      this.fuzzySearchResultContainerTarget.classList.remove("hidden");
      this.fuzzySearchResultContainerTarget.style.display = "block";
    }
  }

  clearGroup() {
    if (this.hasGroupTarget) {
      this.groupTarget.value = ""
    }
  }

  clearFilters() {
    if (this.hasFuzzySearchFilterTarget) {
      this.fuzzySearchFilterTargets.forEach((target) => {
        if (target.type === 'checkbox'){
          target.checked = false
        } else {
          target.value = ""
        }
      })
      this.submit()
    }
  }

  searchByTag(event) {
    const tag = event.currentTarget.dataset.tag
    if (!tag || !this.hasFuzzySearchSubmitTarget) return

    this.fuzzySearchSubmitTarget.value = tag
    this.fuzzySearchSubmitTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  async submit() {
    const mode = this.hasSearchModeTarget ? this.searchModeTarget.value : "normal";

    if (mode === "normal") {
      await asyncFormSubmit(this.element);
      const hasQuery = this.hasFuzzySearchSubmitTarget && (this.fuzzySearchSubmitTarget as HTMLInputElement).value.trim().length > 0;
      if (this.hasFuzzySearchResultContainerTarget && hasQuery) {
        this.fuzzySearchResultContainerTarget.classList.remove("hidden");
        this.fuzzySearchResultContainerTarget.style.display = "block";
      }
    }
  }

  toggleMode(event: Event) {
    const button = event.currentTarget as HTMLElement;
    const mode = button.dataset.mode;

    if (this.hasSearchModeTarget && mode) {
      this.searchModeTarget.value = mode;
      this.updateModeButtons();
      this.updatePlaceholder();
      this.updateActionButton();

      if (mode === "ai") {
        this.hideFuzzySearchResultContainer();
      }
    }
  }

  updatePlaceholder() {
    if (!this.hasFuzzySearchSubmitTarget) return;

    const mode = this.hasSearchModeTarget ? this.searchModeTarget.value : "normal";
    const inputElement = this.fuzzySearchSubmitTarget as HTMLInputElement;

    if (mode === "ai") {
      inputElement.placeholder = "Stelle eine Frage zu deinen HR-Daten...";
    } else {
      inputElement.placeholder = inputElement.dataset.defaultPlaceholder ?? "Themen finden…";
    }
  }

  updateModeButtons() {
    if (!this.hasSearchModeTarget || !this.hasModeButtonTarget) return;

    const currentMode = this.searchModeTarget.value;

    this.modeButtonTargets.forEach(button => {
      const buttonMode = button.dataset.mode;
      if (buttonMode === currentMode) {
        button.classList.remove('bg-white', 'text-gray-600', 'hover:bg-gray-50', 'border-gray-300');
        button.classList.add('bg-blue-600', 'text-white', 'hover:bg-blue-700', 'border-blue-600');
      } else {
        button.classList.remove('bg-blue-600', 'text-white', 'hover:bg-blue-700', 'border-blue-600');
        button.classList.add('bg-white', 'text-gray-600', 'hover:bg-gray-50', 'border-gray-300');
      }
    });
  }

  updateActionButton() {
    if (this.hasActionButtonLabelTarget) {
      const mode = this.hasSearchModeTarget ? this.searchModeTarget.value : "normal";
      (this.actionButtonLabelTarget as HTMLElement).textContent = mode === "ai" ? "Start" : "Suche";
    }
  }

  handleActionButton() {
    const mode = this.hasSearchModeTarget ? this.searchModeTarget.value : "normal";

    if (mode === "ai") {
      this.generateDashboard();
    } else {
      this.submit();
    }
  }

  hideFuzzySearchResultContainer() {
    if (this.hasFuzzySearchResultContainerTarget) {
      this.fuzzySearchResultContainerTarget.style.display = "none";
    }
  }

  async generateDashboard() {
    const queryInput = this.fuzzySearchSubmitTarget as HTMLInputElement;
    const query = queryInput.value.trim();

    if (query.length === 0) return;

    this.showLoadingOverlay();

    queryInput.disabled = true;
    queryInput.style.opacity = '0.6';

    if (this.hasActionButtonTarget) {
      const button = this.actionButtonTarget as HTMLButtonElement;
      button.disabled = true;
      button.classList.add('opacity-50', 'cursor-not-allowed');
    }
    if (this.hasActionButtonLabelTarget) {
      (this.actionButtonLabelTarget as HTMLElement).textContent = "Generiere\u2026";
    }
    if (this.hasActionButtonIconTarget) {
      (this.actionButtonIconTarget as HTMLElement).innerHTML = '<svg class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>';
    }

    this.modeButtonTargets.forEach(button => {
      button.setAttribute('disabled', 'true');
      button.style.opacity = '0.6';
      button.style.cursor = 'not-allowed';
    });

    const sectionName = (this.element.querySelector('#ai_section_name') as HTMLInputElement)?.value;
    const sectionId = (this.element.querySelector('input[name="section_id"]') as HTMLInputElement)?.value;
    const studyKey = (document.querySelector('input[name="study_key"]') as HTMLInputElement)?.value;

    const params = new URLSearchParams();
    params.append('query', query);
    if (studyKey) params.append('study_key', studyKey);
    if (sectionId) params.append('section_id', sectionId);
    if (sectionName) params.append('section_name', sectionName);

    const generateUrl = this.buildDiscoveriesUrl();

    try {
      const response = await fetch(generateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-CSRF-Token': (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content || ''
        },
        body: params.toString()
      });

      const result = await response.json();

      if (!response.ok) {
        console.error('[Discovery] Error:', result.error);
        this.hideOverlay();
        alert(`Fehler: ${result.error || 'Unbekannter Fehler'}`);
        this.resetButtonState();
      }
    } catch (error) {
      console.error('[Discovery] Fetch error:', error);
      this.hideOverlay();
      alert('Fehler bei der Anfrage. Bitte versuche es erneut.');
      this.resetButtonState();
    }
  }

  private buildDiscoveriesUrl(): string {
    const accountId = (this.element.querySelector('#ai_account_id') as HTMLInputElement)?.value;
    const productIdentifier = (this.element.querySelector('#ai_product_identifier') as HTMLInputElement)?.value;
    const sectionName = (this.element.querySelector('#ai_section_name') as HTMLInputElement)?.value;
    return `/c/${accountId}/products/${productIdentifier}/${sectionName}/discoveries`;
  }

  private async cancelDiscovery() {
    const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content || "";
    try {
      await fetch(`${this.buildDiscoveriesUrl()}/cancel`, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken }
      });
    } catch {
      // best effort – reload to a clean state regardless
    }
    window.location.reload();
  }

  private showLoadingOverlay() {
    const frame = document.getElementById("discovery_progress_overlay");
    if (!frame) return;
    frame.innerHTML = `
      <div class="fixed inset-0 z-50 flex items-center justify-center bg-white/70 backdrop-blur-sm">
        <div class="relative bg-white rounded-2xl shadow-2xl border border-gray-100 px-10 py-12 max-w-md w-full mx-4 text-center">
          <button type="button" data-discovery-cancel aria-label="Analyse abbrechen"
                  class="absolute top-3 right-3 p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
            <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          <div class="inline-flex items-center justify-center w-16 h-16 mb-6">
            <svg class="animate-spin w-12 h-12 text-blue-600" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-opacity="0.2" stroke-width="3"></circle>
              <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
            </svg>
          </div>
          <p class="text-lg font-medium text-gray-900 mb-2 min-h-7">Analyse wird gestartet...</p>
          <p class="text-sm text-gray-500">Dies kann 30 bis 60 Sekunden dauern.</p>
        </div>
      </div>
    `;
    frame.querySelector("[data-discovery-cancel]")?.addEventListener("click", () => this.cancelDiscovery());
  }

  private hideOverlay() {
    const frame = document.getElementById("discovery_progress_overlay");
    if (frame) frame.innerHTML = "";
  }

  resetButtonState() {
    const queryInput = this.fuzzySearchSubmitTarget as HTMLInputElement;
    queryInput.disabled = false;
    queryInput.style.opacity = '1';

    if (this.hasActionButtonTarget) {
      const button = this.actionButtonTarget as HTMLButtonElement;
      button.disabled = false;
      button.classList.remove('opacity-50', 'cursor-not-allowed');
    }
    this.updateActionButton();

    this.modeButtonTargets.forEach(button => {
      button.removeAttribute('disabled');
      button.style.opacity = '1';
      button.style.cursor = 'pointer';
    });
  }
}
